<template>
  <div class="page-view">
    <BasePageHeader :title="$t('maintenance.complete.title')" @back="router.back()">
      <template #actions>
        <button class="btn ghost small" @click="router.back()">{{ $t('common.cancel') }}</button>
        <button class="btn primary small" :disabled="saving || !isValid" @click="submit">
          {{ saving ? $t('common.saving') : $t('maintenance.markComplete') }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>

    <template v-else>

      <!-- Schedule context banner -->
      <div v-if="schedule" class="px-4 pt-4">
        <div class="flex flex-col gap-1 px-3.5 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-sm">
          <div class="text-[var(--color-muted)] text-xs uppercase tracking-wide font-semibold">{{ $t('maintenance.complete.completing') }}</div>
          <div class="font-semibold text-[var(--color-text)]">{{ schedule.title }}</div>
          <div v-if="schedule.items?.length" class="flex flex-wrap gap-1 mt-1">
            <span v-for="inst in schedule.items" :key="inst.id ?? inst" class="badge badge-muted badge-pill">{{ instanceLabel(inst) }}</span>
          </div>
        </div>
      </div>

      <!-- Record details -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('maintenance.complete.serviceRecord') }}</div>

        <BaseFormField :label="$t('maintenance.form.title')" required>
          <BaseInput v-model="form.title" :placeholder="$t('ui.eGRsservicePumpeA2026')" />
        </BaseFormField>

        <BaseFormField :label="$t('common.description')" required>
          <BaseTextarea v-model="form.description" placeholder="Sammendrag av arbeidet…" :rows="3" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('maintenance.complete.serviceDate')" required>
            <BaseInput v-model="form.service_date" type="date" />
          </BaseFormField>
          <BaseFormField :label="$t('common.status')">
            <BaseSelect v-model="form.status">
              <option value="completed">{{ $t('maintenance.complete.statusCompleted') }}</option>
              <option value="in_progress">{{ $t('maintenance.complete.statusInProgress') }}</option>
              <option value="failed">{{ $t('maintenance.complete.statusFailed') }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>

        <div class="form-row">
          <BaseFormField :label="$t('maintenance.complete.actualHours')">
            <BaseInput v-model="form.actual_hours" type="number" min="0" step="0.5" placeholder="0" />
          </BaseFormField>
          <BaseFormField :label="$t('maintenance.complete.cost')">
            <BaseInput v-model="form.cost" type="number" min="0" step="0.01" placeholder="0.00" />
          </BaseFormField>
        </div>
      </div>

      <!-- Findings & actions -->
      <div class="px-4 pt-5 pb-4 border-b border-[var(--color-border)]">
        <div class="section-label">{{ $t('maintenance.complete.findings') }}</div>

        <BaseFormField :label="$t('maintenance.complete.findingsLabel')">
          <BaseTextarea v-model="form.findings" placeholder="Funn underveis…" :rows="2" />
        </BaseFormField>

        <BaseFormField :label="$t('maintenance.complete.actionsTaken')">
          <BaseTextarea v-model="form.actions_taken" placeholder="Hva ble utbedret…" :rows="2" />
        </BaseFormField>

        <BaseFormField :label="$t('maintenance.complete.partsReplaced')">
          <BaseInput v-model="form.parts_replaced" placeholder="Del A, Del B…" />
        </BaseFormField>
      </div>

      <!-- Error -->
      <div v-if="saveError" class="mx-4 mt-3 state-empty error">{{ saveError }}</div>

    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { getMaintenanceScheduleById, markMaintenanceScheduleComplete } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import EmptyState from '@/components/base/EmptyState.vue'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()

const scheduleId = route.params.id

const loading   = ref(true)
const loadError = ref(null)
const saving    = ref(false)
const saveError = ref(null)

const schedule  = ref(null)

const form = ref({
  title:         '',
  description:   '',
  service_date:  new Date().toISOString().slice(0, 10),
  status:        'completed',
  actual_hours:  '',
  cost:          '',
  findings:      '',
  actions_taken: '',
  parts_replaced:'',
})

function instanceLabel(inst) {
  if (!inst) return ''
  if (typeof inst === 'object') return inst.asset_tag ?? inst.serial_number ?? inst.item?.name ?? `#${inst.id}`
  return `#${inst}`
}

onMounted(async () => {
  try {
    const s = await getMaintenanceScheduleById(scheduleId)
    schedule.value = s
    form.value.title            = s.title ?? ''
    form.value.actual_hours     = s.estimated_hours ?? ''
    if (s.maintenance_type) form.value.maintenance_type = s.maintenance_type
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('maintenance.complete.loadFailed')
  } finally {
    loading.value = false
  }
})

const isValid = computed(() =>
  form.value.title.trim() &&
  form.value.description.trim() &&
  form.value.service_date
)

async function submit() {
  if (saving.value || !isValid.value) return
  saving.value    = true
  saveError.value = null

  const s       = schedule.value
  const itemIds = (s?.items?.length)
    ? s.items.map(i => i.id ?? i)
    : (s?.item_metadata ? [s.item_metadata?.id ?? s.item_metadata] : [])

  try {
    const payload = {
      title:         form.value.title.trim(),
      description:   form.value.description.trim(),
      service_date:  form.value.service_date,
      item_metadata: itemIds,
      status:        form.value.status,
    }
    if (form.value.maintenance_type)       payload.maintenance_type = form.value.maintenance_type
    if (form.value.actual_hours !== '')    payload.actual_hours     = String(form.value.actual_hours)
    if (form.value.cost !== '')            payload.cost             = String(form.value.cost)
    if (form.value.findings?.trim())       payload.findings         = form.value.findings.trim()
    if (form.value.actions_taken?.trim())  payload.actions_taken    = form.value.actions_taken.trim()
    if (form.value.parts_replaced?.trim()) payload.parts_replaced   = form.value.parts_replaced.trim()

    await markMaintenanceScheduleComplete(scheduleId, payload)
    toast.success(t('maintenance.complete.saved'))
    router.push('/maintenance/schedules')
  } catch (e) {
    const d = e?.data
    if (d && typeof d === 'object') {
      const msgs = Object.entries(d)
        .filter(([k]) => k !== 'status_code')
        .flatMap(([, v]) => Array.isArray(v) ? v : [v])
        .filter(Boolean)
        .join(' ')
      saveError.value = msgs || e?.message || t('maintenance.complete.saveFailed')
    } else {
      saveError.value = e?.message ?? t('maintenance.complete.saveFailed')
    }
  } finally {
    saving.value = false
  }
}
</script>
